<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-inbox-in"></i>
        {{ $t("fleetTrips.incoming_title") }}
      </h4>
    </div>

    <div class="box">
      <div class="columns">
        <div class="column is-3">
          <div class="control field">
            <label class="label">{{ $t("fleetTrips.date") }}:</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="incomingDate"
                :clearable="false"
                @change="OnDateChange"
            ></date-picker>
          </div>
        </div>
        <!-- Plan-based picker: route -> trip -> planned stop (no name recall
             needed). Hidden for branch/DC staff — their facility is auto and
             the backend will never serve them another one (facility_locked),
             so offering a foreign stop could only mislead. -->
        <div class="column is-3" v-if="StopPickerAllowed">
          <div class="control">
            <label class="label">{{ $t("fleetTrips.route") }}</label>
            <div class="select" :style="{width: '100%'}">
              <select class="input" v-model="pickerRouteId" @change="FetchPickerTrips()">
                <option v-for="(route, index) in boardRoutes" :key="index" :value="route.id">
                  {{ route.code ? `${route.code} · ${route.name}` : route.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div class="column is-3" v-if="StopPickerAllowed">
          <div class="control">
            <label class="label">{{ $t("fleetTrips.trip") }}</label>
            <div class="select" :style="{width: '100%'}">
              <select class="input" v-model="pickerTripId" @change="OnPickerTripChange()">
                <option value="">{{ $t("fleetTrips.select_trip_placeholder") }}</option>
                <option v-for="trip in pickerTrips" :key="trip.id" :value="trip.id">
                  [{{ trip.direction === 'return' ? $t("fleetTrips.direction_return") : $t("fleetTrips.direction_outbound") }}]
                  {{ trip.route_name }} {{ $t("fleetTrips.round_n", {n: trip.round_number}) }} · {{ trip.trip_code }}
                  {{ trip.truck_plate ? '· ' + trip.truck_plate : '' }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div class="column is-3" v-if="StopPickerAllowed && pickerTripId">
          <div class="control">
            <label class="label">{{ $t("fleetTrips.stop_in_trip") }}</label>
            <div class="select" :style="{width: '100%'}">
              <select class="input" v-model="pickerStopIndex" @change="OnPickerStopChange">
                <option value="">{{ $t("fleetTrips.select_stop_placeholder") }}</option>
                <option v-for="(stop, index) in pickerStops" :key="index" :value="index">
                  {{ stop.sequence }}. {{ PlannedStopName(stop) }}
                  ({{ FleetStopTypeLabel(stop.stop_type) }})
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="columns" v-if="StopPickerAllowed">
        <div class="column is-2">
          <div class="control">
            <label class="label">{{ $t("fleetTrips.stop_type") }}</label>
            <div class="select">
              <select class="input" v-model="facilityType" @change="SearchFacilities">
                <option value="branch">{{ $t("common.ສາຂາ") }}</option>
                <option value="distribution_center">{{ $t("common.ສູນຄັດແຍກ") }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="column is-4">
          <div class="control">
            <label class="label">{{ $t("fleetTrips.or_search_stop_directly") }}</label>
            <input class="input" :placeholder="$t('fleetTrips.search_stop_name_placeholder')" v-model="facilitySearch"
                   @keyup.enter="SearchFacilities">
            <div class="facility-results" v-if="facilityOptions.length">
              <div class="facility-row" v-for="(facility, index) in facilityOptions" :key="index"
                   @click="SelectFacility(facility)">
                {{ facility.name }} <span :style="{color: 'grey'}">({{ facility.prefix }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- The stop the rows below actually belong to. A rostered account is
           always answered with its OWN branch/DC whatever was asked for, so
           this reads the backend's `data.facility`, never the pick. Its own row
           so it survives the search block being hidden for those accounts. -->
      <div class="columns" v-if="ServedFacility || selectedFacility">
        <div class="column is-6">
          <div class="control">
            <label class="label">{{ $t("fleetTrips.served_stop") }}</label>
            <p :style="{fontWeight: 'bold', color: FacilityIgnored ? '#e8710a' : '#195BFF', paddingTop: '6px'}">
              {{ ServedFacilityName }}
              <span :style="{color: 'grey', fontSize: '12px'}" v-if="ServedFacility">
                ({{ FleetStopTypeLabel(ServedFacility.type) }})
              </span>
              <span :style="{color: 'grey', fontSize: '12px'}" v-if="facilityLocked">
                · {{ $t("fleetTrips.account_locked_to_stop") }}
              </span>
            </p>
          </div>
        </div>
      </div>
      <p v-if="needFacility" :style="{color: '#e8a10f', fontSize: '13px'}">
        <i class="fal fa-info-circle"></i> {{ $t("fleetTrips.need_facility_hint") }}
      </p>
      <p v-if="FacilityIgnored" :style="{color: '#e8710a', fontSize: '13px'}">
        <i class="fal fa-exclamation-triangle"></i>
        {{ $t("fleetTrips.facility_ignored_prefix") }} <b>{{ ServedFacilityName }}</b> {{ $t("fleetTrips.facility_ignored_middle") }}
        <b>{{ selectedFacility.name || (FleetStopTypeLabel(selectedFacility.type) + ' #' + selectedFacility.id) }}</b>
      </p>
    </div>

    <!-- Route map of the incoming trips (same controls as the route builder) -->
    <div class="box" v-if="incomingTrips.length">
      <label class="label"><i class="fal fa-map-marked-alt"></i> {{ $t("fleetTrips.incoming_trips_map") }}</label>
      <div class="trip-chips">
        <span v-for="(row, index) in incomingTrips" :key="index"
              class="trip-chip" :class="{active: row.trip_id === mapTripId}"
              @click="mapTripId = row.trip_id">
          {{ row.trip_code }}
          <small v-if="row.truck_plate">· {{ row.truck_plate }}</small>
        </span>
      </div>
      <FleetStopsMap v-if="incomingMapStops.length"
                     :stops="incomingMapStops" :checkins="incomingMapCheckins" height="380px"/>
      <p v-else :style="{color: 'grey', fontSize: '13px'}">
        {{ $t("fleetTrips.stops_no_coordinates") }}
      </p>
      <p v-if="incomingMapStops.length" :style="{fontSize: '12px', color: 'grey', marginTop: '5px'}">
        <i class="fas fa-circle" :style="{color: 'crimson'}"></i> {{ $t("fleetTrips.legend_my_stop") }}
        <i class="fas fa-circle" :style="{color: '#2dcc70', marginLeft: '10px'}"></i> {{ $t("fleetTrips.legend_start_point") }}
        <i class="fas fa-circle" :style="{color: '#195BFF', marginLeft: '10px'}"></i> {{ $t("common.ສາຂາ") }}
        <i class="fas fa-circle" :style="{color: '#e8a10f', marginLeft: '10px'}"></i> {{ $t("common.ສູນຄັດແຍກ") }}
        <span v-if="incomingMapCheckins.length">
          <i class="fas fa-circle" :style="{color: '#2dcc70', marginLeft: '10px', fontSize: '9px'}"></i>
          {{ $t("fleetTrips.legend_driver_checkin") }}
        </span>
      </p>
    </div>

    <div class="center">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t("fleetTrips.trip_code") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.route") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.round") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.truck_plate") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.planned_arrival") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.actual_arrival") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.incoming_pieces_bills_kg") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.delivered") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.still_on_truck") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.wrong_stop") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.off_route") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.status") }}</vs-th>
            <vs-th></vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="index" :data="row" v-for="(row, index) in incomingTrips">
            <vs-td :style="{color: 'crimson', fontWeight: 'bold'}">
              {{ row.trip_code }}
              <span v-if="row.direction"
                    :style="{color: FleetDirectionColor(row.direction), fontSize: '11px', fontWeight: 700}">
                [{{ FleetDirectionLabel(row.direction) }}]
              </span>
            </vs-td>
            <vs-td :style="{color: '#195BFF'}">{{ row.route_name }}</vs-td>
            <vs-td :style="{textAlign: 'center'}">{{ row.round_number }}</vs-td>
            <vs-td>{{ row.truck_plate || '...' }}</vs-td>
            <vs-td>{{ FleetFormatTime(row.planned_arrival_at) }}</vs-td>
            <vs-td>{{ FleetFormatTime(row.checked_in_at) }}</vs-td>
            <vs-td>
              {{ row.incoming ? row.incoming.pieces : 0 }} /
              {{ row.incoming ? row.incoming.bills : 0 }} /
              {{ FleetFormatNumber(row.incoming ? row.incoming.weight_kg : 0, 1) }}
            </vs-td>
            <vs-td :style="{color: '#2dcc70'}">{{ row.delivered }}</vs-td>
            <vs-td :style="{color: '#195BFF'}">{{ row.still_on_truck }}</vs-td>
            <vs-td :style="{color: row.wrong_stop ? 'crimson' : ''}">{{ row.wrong_stop }}</vs-td>
            <vs-td :style="{color: OffRoutePieces(row) ? '#e8a10f' : '', fontWeight: OffRoutePieces(row) ? 'bold' : ''}"
                   :title="OffRoutePieces(row) ? $t('fleetTrips.off_route_in_trip_tooltip') : ''">
              <span v-if="OffRoutePieces(row)">
                {{ FleetOffRouteText(OffRoutePieces(row), row.off_route && row.off_route.on_truck_pieces) }}
              </span>
              <span v-else :style="{color: 'grey'}">0</span>
            </vs-td>
            <vs-td>
              <span class="status-chip" :style="{background: FleetTripStatusColor(row.trip_status)}">
                {{ FleetTripStatusLabel(row.trip_status) }}
              </span>
            </vs-td>
            <vs-td style="text-align: right; width: 60px">
              <vs-button circle icon flat @click="GoToTripDetail(row.trip_id)">
                <i class="fal fa-eye"></i>
              </vs-button>
            </vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <Loading v-if="incomingLoading"/>
          <p v-if="!incomingLoading && !incomingTrips.length"
             :style="{textAlign: 'center', color: 'grey', padding: '15px'}">
            {{ $t("fleetTrips.no_incoming_trips") }}
          </p>
        </template>
      </vs-table>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import Loading from '@views/Loading';
import MixinFleetTrips from '@/Mixins/Fleet/Trips';
import {FLEET_BASE} from '@/Mixins/Fleet/FleetHelpers';
import FleetStopsMap from '@views/SuperAdmin/Fleet/FleetStopsMap';

export default {
  components: {
    DatePicker,
    Loading,
    FleetStopsMap,
  },
  mixins: [MixinFleetTrips],
  data: () => ({
    incomingDate: moment(new Date()).format('YYYY-MM-DD'),
    incomingTrips: [],
    incomingLoading: false,
    needFacility: false,
    mapTripId: null,

    facilityType: 'branch',
    facilitySearch: '',
    facilityOptions: [],
    selectedFacility: null,
    // `data.facility` from the last response = the stop the rows are FOR.
    // Null on an older backend that doesn't send it (then the picked facility
    // is displayed, exactly as before).
    servedFacility: null,
    // `data.facility_locked` — this account is tied to its own branch/DC and
    // the backend will never serve it another, so no stop may be offered.
    facilityLocked: false,

    // Plan-based picker (mgmt only — staff facility is auto-resolved)
    pickerAllowed: true,
    pickerRouteId: '',
    pickerTrips: [],
    pickerTripId: '',
    pickerStops: [],
    pickerStopIndex: '',
  }),
  computed: {
    /**
     * May this account be offered ANOTHER stop? Only a facility-less management
     * caller may — for everyone else the backend answers with their own stop
     * regardless, so a picker could only produce a request that is ignored.
     * `pickerAllowed` stays as the separate 403 fallback for the mgmt-only
     * trips endpoint the plan picker reads.
     */
    StopPickerAllowed() {
      return this.pickerAllowed && !this.facilityLocked;
    },
    /** The facility the backend actually answered with (null on old backends). */
    ServedFacility() {
      return this.servedFacility;
    },
    /**
     * True when the backend served a DIFFERENT stop than the one picked — a
     * rostered account (nearly every DC manager) is always answered with its
     * own branch/DC, and the pick is ignored rather than refused.
     */
    FacilityIgnored() {
      return !!this.selectedFacility
          && !!this.servedFacility
          && !this.SameFacility(this.selectedFacility, this.servedFacility);
    },
    /**
     * Name of the served stop: from the returned trips' own stop row (the
     * backend already resolves names there), else the picked name when that IS
     * what was served, else a type + #id fallback.
     */
    ServedFacilityName() {
      const served = this.servedFacility;

      if (!served) {
        return (this.selectedFacility && this.selectedFacility.name) || '';
      }

      const named = this.StopNameFromTrips(served);
      if (named) return named;

      if (this.selectedFacility && this.SameFacility(this.selectedFacility, served) && this.selectedFacility.name) {
        return this.selectedFacility.name;
      }

      return `${this.FleetStopTypeLabel(served.type)} #${served.id}`;
    },
    mapTrip() {
      return this.incomingTrips.find((row) => row.trip_id === this.mapTripId) || null;
    },
    incomingMapStops() {
      if (!this.mapTrip) return [];
      return (this.mapTrip.stops || []).map((stop) => ({
        lat: stop.lat,
        lng: stop.lng,
        type: stop.stop_type,
        label: stop.sequence,
        title: stop.name || `${stop.stop_type} #${stop.stop_id}`,
        isStart: stop.stop_role === 'pickup' && stop.sequence === 1,
        isMine: !!stop.is_mine,
      })).filter((stop) => Number(stop.lat) && Number(stop.lng));
    },
    incomingMapCheckins() {
      if (!this.mapTrip) return [];
      return (this.mapTrip.stops || [])
          .filter((stop) => Number(stop.checkin_lat) && Number(stop.checkin_lng))
          .map((stop) => ({
            lat: Number(stop.checkin_lat),
            lng: Number(stop.checkin_lng),
            title: `Check-in: ${stop.name || ''}`,
          }));
    },
  },
  methods: {
    // Keep the picker state in the URL query so navigating to a trip detail
    // and coming back (or sharing the link) restores the same filtered view.
    SyncQuery() {
      const query = {};
      if (this.incomingDate) query.date = this.incomingDate;
      if (this.pickerRouteId) query.route_id = String(this.pickerRouteId);
      if (this.pickerTripId) query.trip_id = String(this.pickerTripId);
      if (this.selectedFacility) {
        query.stop_type = this.selectedFacility.type;
        query.stop_id = String(this.selectedFacility.id);
        if (this.selectedFacility.name) query.stop_name = this.selectedFacility.name;
      }
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({query}).catch(() => {});
      }
    },
    OnDateChange() {
      this.FetchIncoming().then(() => this.FetchPickerTrips());
    },
    FetchPickerTrips(preserveSelection = false) {
      if (!this.StopPickerAllowed) return;
      if (!preserveSelection) {
        this.pickerTripId = '';
        this.pickerStops = [];
        this.pickerStopIndex = '';
        this.SyncQuery();
      }
      this.$http.get(`${FLEET_BASE}/trips`, {
        params: {date: this.incomingDate, route_id: this.pickerRouteId || undefined, per_page: 100},
      }).then((res) => {
        this.pickerTrips = res.data.data || [];
        if (preserveSelection && this.pickerTripId) {
          this.OnPickerTripChange(true);
        }
      }).catch((error) => {
        this.pickerTrips = [];
        // Branch/DC staff cannot list trips — hide the plan picker for them.
        if (error.response && error.response.status === 403) {
          this.pickerAllowed = false;
        }
      });
    },
    OnPickerTripChange(preserveSelection = false) {
      this.pickerStops = [];
      this.pickerStopIndex = '';
      if (!preserveSelection) this.SyncQuery();
      if (!this.pickerTripId) return;
      this.$http.get(`${FLEET_BASE}/trips/${this.pickerTripId}/detail`).then((res) => {
        this.pickerStops = ((res.data.data && res.data.data.stops) || [])
            .slice()
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        // Re-highlight the restored facility in the stop dropdown.
        if (preserveSelection && this.selectedFacility) {
          const index = this.pickerStops.findIndex((stop) => stop.stop_type === this.selectedFacility.type
              && Number(stop.stop_id) === Number(this.selectedFacility.id));
          if (index >= 0) this.pickerStopIndex = index;
        }
      }).catch(() => {
      });
    },
    OnPickerStopChange() {
      if (this.pickerStopIndex === '') return;
      const stop = this.pickerStops[this.pickerStopIndex];
      if (!stop) return;
      this.SelectFacility({type: stop.stop_type, id: stop.stop_id, name: this.PlannedStopName(stop)});
    },
    PlannedStopName(stop) {
      return (stop.facility && stop.facility.name)
          || `${this.FleetStopTypeLabel(stop.stop_type)} #${stop.stop_id}`;
    },
    SearchFacilities() {
      if (!this.facilitySearch) {
        this.facilityOptions = [];
        return;
      }
      this.$http.get(`${FLEET_BASE}/stop-points`, {
        params: {q: this.facilitySearch, type: this.facilityType, limit: 15},
      }).then((res) => {
        this.facilityOptions = res.data.data || [];
      }).catch(() => {
      });
    },
    SelectFacility(facility) {
      // Belt and braces: the controls are hidden for a locked account, but a
      // restored URL or a stale option must not set a stop we cannot be served.
      if (this.facilityLocked) return;
      this.selectedFacility = facility;
      this.facilityOptions = [];
      this.facilitySearch = facility.name;
      this.SyncQuery();
      this.FetchIncoming();
    },
    FetchIncoming() {
      this.incomingLoading = true;
      const params = {date: this.incomingDate};
      // NB: returns the promise — the plan picker's own (management-only)
      // lookups wait on it, so a locked account never fires them at all.
      if (this.selectedFacility) {
        params.stop_type = this.selectedFacility.type;
        params.stop_id = this.selectedFacility.id;
      }
      return this.FetchStopsIncoming(params).then((res) => {
        const data = res.data.data || {};
        this.incomingTrips = data.trips || [];
        // The stop these rows are FOR — a rostered account always gets its own
        // facility whatever it asked for, so never label them with the pick.
        this.servedFacility = data.facility || null;
        this.facilityLocked = !!data.facility_locked;
        // A locked account cannot be served the stop it asked for, so drop a
        // pick restored from the URL rather than leaving a stop on screen that
        // is not what the rows describe. Same tick as the data, so no flash.
        if (this.facilityLocked && this.selectedFacility
            && !this.SameFacility(this.selectedFacility, this.servedFacility)) {
          this.selectedFacility = null;
          this.facilitySearch = '';
          this.facilityOptions = [];
          this.SyncQuery();
        }
        if (!this.incomingTrips.some((row) => row.trip_id === this.mapTripId)) {
          this.mapTripId = this.incomingTrips.length ? this.incomingTrips[0].trip_id : null;
        }
        this.needFacility = false;
        this.incomingLoading = false;
      }).catch((error) => {
        this.incomingLoading = false;
        this.incomingTrips = [];
        this.servedFacility = null;
        // Management callers without a facility must pick one (422 from backend).
        if (error.response && error.response.status === 422 && !this.selectedFacility) {
          this.needFacility = true;
        }
      });
    },
    SameFacility(a, b) {
      return !!a && !!b && a.type === b.type && Number(a.id) === Number(b.id);
    },
    /** Resolve a facility's display name from the incoming trips' stop rows. */
    StopNameFromTrips(facility) {
      for (const trip of this.incomingTrips) {
        const stop = (trip.stops || []).find((row) => row.stop_type === facility.type
            && Number(row.stop_id) === Number(facility.id));
        if (stop && stop.name) return stop.name;
      }
      return '';
    },
    OffRoutePieces(row) {
      return (row && row.off_route && row.off_route.pieces) || 0;
    },
    GoToTripDetail(tripId) {
      this.$router.push({name: 'fleet.trips.detail', params: {id: tripId}});
    },
  },
  created() {
    // Restore any state carried in the URL (back from trip detail, shared link).
    const query = this.$route.query || {};
    if (query.date) this.incomingDate = query.date;
    if (query.route_id) this.pickerRouteId = Number(query.route_id) || query.route_id;
    if (query.trip_id) this.pickerTripId = Number(query.trip_id) || '';
    if (query.stop_type && query.stop_id) {
      this.selectedFacility = {
        type: query.stop_type,
        id: Number(query.stop_id),
        name: query.stop_name || '',
      };
      this.facilitySearch = query.stop_name || '';
    }

    // The stop picker is management-only, so wait for the first response to
    // say whether this account may be offered one before loading its data.
    this.FetchIncoming().then(() => {
      if (!this.StopPickerAllowed) return;
      this.FetchBoardRoutes();
      this.FetchPickerTrips(!!this.pickerTripId);
    });
  },
};
</script>

<style lang="scss" scoped>
.status-chip {
  color: white;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
}

.trip-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;

  .trip-chip {
    border: 1px solid #d5ddf3;
    border-radius: 16px;
    padding: 4px 14px;
    font-size: 13px;
    font-weight: bold;
    color: #195BFF;
    cursor: pointer;

    small {
      color: grey;
      font-weight: normal;
    }

    &.active {
      background: #195BFF;
      color: white;

      small {
        color: #d5ddf3;
      }
    }
  }
}

.facility-results {
  border: 1px solid #eee;
  border-radius: 4px;
  max-height: 180px;
  overflow-y: auto;
  background: white;
  position: relative;
  z-index: 5;
}

.facility-row {
  padding: 6px 10px;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 1px solid #f4f4f4;

  &:hover {
    background: #eef4ff;
  }
}
</style>
